// NOTE: relative imports (not "@/..." or "convex/...") because vitest.config.ts
// does not register the "@" path alias and the unit tests must run under vitest.
// The engine is pure TypeScript shared between backend (convex/) and frontend.
import {
  calculateBmi,
  calculateBmr,
  calculateCalorieTarget,
  calculateInitialTdee,
  calculateMacroTargets,
  simulateGoalTimeline,
  type EngineProfile,
} from "../../convex/lib/engine";
import type { FlowAnswers } from "./types";

export type OnboardingGoal = "lose" | "maintain" | "gain" | "healthier" | "muscle";
export type OnboardingPace = "relaxed" | "steady" | "aggressive";
export type OnboardingDietType = "keto" | "low-carb" | "balanced" | "high-protein" | "custom";

export interface OnboardingAnswers {
  readonly goalType?: OnboardingGoal;
  readonly weightKg?: number;
  readonly targetWeightKg?: number;
  readonly heightCm?: number;
  readonly age?: number;
  readonly gender?: string;
  readonly activityLevel?: string;
  readonly exerciseFrequency?: string;
  readonly exerciseType?: string;
  readonly avgDailySteps?: number;
  readonly weightLossPace?: OnboardingPace;
  readonly eatingStyle?: string;
  readonly bodyFatPercent?: number;
  readonly waistCm?: number;
  readonly safetyFlags?: readonly string[];
}

export interface OnboardingPlan {
  readonly dailyKcal: number;
  readonly proteinG: number;
  readonly carbsG: number;
  readonly fatG: number;
  readonly fiberG: number;
  readonly weeklyRateKg: number;
  readonly weeksToGoal: number;
  readonly dietType: OnboardingDietType;
  readonly weeksMin?: number;
  readonly weeksMax?: number;
  readonly goalDateMin?: number;
  readonly goalDateMax?: number;
  readonly wasConstrained?: boolean;
  readonly restricted?: boolean;
  readonly constraintReasons?: string[];
  readonly bmi?: number;
  readonly bmiClass?: string;
  readonly bmrKcal?: number;
  readonly tdeeKcal?: number;
}

export const PACE_LABELS: Record<OnboardingPace, string> = {
  relaxed: "Comfortable",
  steady: "Moderate",
  aggressive: "Aggressive",
};

/** Display reference for the pace weekly rates (% of body weight per week). */
export const PACE_WEEKLY_RATES: Record<"lose" | "gain", Record<OnboardingPace, number>> = {
  lose: { relaxed: 0.25, steady: 0.5, aggressive: 0.75 },
  gain: { relaxed: 0.15, steady: 0.25, aggressive: 0.4 },
};

const FALLBACK_KCAL = 2000;
const MIN_KCAL = 1200;
const MAX_KCAL = 4000;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function mapDietType(eatingStyle?: string): OnboardingDietType {
  switch (eatingStyle) {
    case "high_protein":
      return "high-protein";
    case "low_carb":
      return "low-carb";
    case "keto":
      return "keto";
    case "other":
      return "custom";
    default:
      // none, vegetarian, vegan, undefined
      return "balanced";
  }
}

/** Project the mutable flow answers onto the engine input consumed by computePlan. */
export function planInputFromAnswers(answers: FlowAnswers): OnboardingAnswers {
  return {
    goalType: answers.goalType,
    weightKg: answers.weightKg,
    targetWeightKg: answers.targetWeightKg,
    heightCm: answers.heightCm,
    age: answers.age,
    gender: answers.gender,
    activityLevel: answers.activityLevel,
    exerciseFrequency: answers.exerciseFrequency,
    exerciseType: answers.exerciseType,
    avgDailySteps: answers.avgDailySteps,
    weightLossPace: answers.weightLossPace,
    eatingStyle: answers.eatingStyle,
    bodyFatPercent: answers.bodyFatPercent,
    waistCm: answers.waistCm,
    safetyFlags: answers.safetyFlags,
  };
}

function buildProfile(answers: OnboardingAnswers, dietType: OnboardingDietType): EngineProfile {
  return {
    weightKg: answers.weightKg,
    heightCm: answers.heightCm,
    age: answers.age,
    gender: answers.gender,
    activityLevel: answers.activityLevel,
    exerciseFrequency: answers.exerciseFrequency,
    exerciseType: answers.exerciseType,
    avgDailySteps: answers.avgDailySteps,
    goalType: answers.goalType,
    targetWeightKg: answers.targetWeightKg,
    weightLossPace: answers.weightLossPace,
    eatingStyle: answers.eatingStyle,
    dietType,
    bodyFatPercent: answers.bodyFatPercent,
    waistCm: answers.waistCm,
    safetyFlags: answers.safetyFlags,
  };
}

/**
 * Compute the onboarding plan by delegating to the deterministic engine.
 * Safety-restricted profiles (safety flags, underweight BMI + lose goal) get a
 * maintenance target with `restricted: true` — never an automated deficit.
 */
export function computePlan(answers: OnboardingAnswers, now = Date.now()): OnboardingPlan {
  const dietType = mapDietType(answers.eatingStyle);
  const profile = buildProfile(answers, dietType);

  const bmiValue = calculateBmi(profile, now)?.value;
  const bmrValue = calculateBmr(profile, now)?.value;
  const tdeeValue = calculateInitialTdee(profile, now)?.value;
  const expenditureKcal = tdeeValue?.kcalPerDay ?? FALLBACK_KCAL;

  const target = calculateCalorieTarget(profile, expenditureKcal, now).value;
  const macros = calculateMacroTargets(profile, target.targetKcal, now).value;
  const timeline = simulateGoalTimeline(profile, expenditureKcal, now).value;

  const dailyKcal = clamp(Math.round(target.targetKcal / 10) * 10, MIN_KCAL, MAX_KCAL);

  console.log("[Frontend][OnboardingPlan] Engine plan computed", {
    dailyKcal,
    weeksToGoal: timeline.weeksToGoal,
    restricted: target.restricted,
    wasConstrained: target.wasConstrained || timeline.extendedForSafety,
    bmi: bmiValue?.bmi ?? null,
  });

  return {
    dailyKcal,
    proteinG: macros.proteinG,
    carbsG: macros.carbsG,
    fatG: macros.fatG,
    fiberG: macros.fiberG,
    weeklyRateKg: Math.abs(timeline.effectiveWeeklyChangeKg),
    weeksToGoal: timeline.weeksToGoal,
    dietType,
    weeksMin: timeline.weeksMin,
    weeksMax: timeline.weeksMax,
    goalDateMin: timeline.goalDateMin,
    goalDateMax: timeline.goalDateMax,
    wasConstrained: target.wasConstrained || timeline.extendedForSafety,
    restricted: target.restricted,
    constraintReasons: [...target.constraintReasons],
    bmi: bmiValue?.bmi,
    bmiClass: bmiValue?.classification,
    bmrKcal: bmrValue?.bmrKcal,
    tdeeKcal: tdeeValue?.kcalPerDay,
  };
}
